import { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getLastPosts } from '../../../../../../store/actions/post';
import Icon from '../../../../../../components/Icon';
import bookSvg from '../../../../../../assets/icons/book.svg';
import videoSvg from '../../../../../../assets/icons/video-recorder.svg';
import calendarSvg from '../../../../../../assets/icons/calendar.svg';
import {
  Section,
  SectionHeader,
  SectionLabel,
  ResultRow,
  ResultText,
  ResultTitle,
  ResultSubtitle,
  SkeletonRow,
  SkeletonIcon,
  SkeletonLine,
} from '../../../SearchResults/styles';
import { EmptyHint } from '../../styles';

const ClockWrap = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.Primary};

  svg {
    width: 15px;
    height: 15px;
  }
`;

interface ComponentProps {
  posts: any[] | null;
  postsLoading: boolean;
  sessions: any[] | null;
  group: any;
}

const Component = ({ posts, postsLoading, sessions, group }: ComponentProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (group?.id) {
      dispatch(getLastPosts({
        pagination: { page: 1, per_parge: 6, _sort: 'desc', _sort_by: 'created_at' },
        filters: { group_id: group.id, show_scheduled_post: true },
      }));
    }
  }, [group?.id]);

  const recentItems = useMemo(() => {
    const items: Array<{
      type: 'post' | 'session';
      id: number;
      title: string;
      subtitle?: string;
      date: string;
      href: string;
      isPast?: boolean;
    }> = [];

    if (posts) {
      posts.forEach(p => items.push({
          type: 'post',
          id: p.id,
          title: p.title,
          subtitle: p.short_description || p.content_categories?.[0]?.name,
          date: p.published_at || '',
          href: `/group/${group?.id}/p/${p.id}`,
        }));
    }

    if (sessions) {
      sessions.forEach(s => items.push({
        type: 'session',
        id: s.id,
        title: s.title,
        subtitle: s.speaker_name || undefined,
        date: s.created_at || '',
        href: `/group/${group?.slug}/s/${s.id}`,
        isPast: s.session_datetime ? new Date(s.session_datetime) < new Date() : true,
      }));
    }

    return items
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [posts, sessions, group]);

  const clockIcon = (
    <ClockWrap>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    </ClockWrap>
  );

  if (postsLoading) {
    return (
      <Section>
        <SectionHeader>
          {clockIcon}
          <SectionLabel>Recientes</SectionLabel>
        </SectionHeader>
        {[55, 70, 45].map((w, i) => (
          <SkeletonRow key={i}>
            <SkeletonIcon />
            <SkeletonLine width={`${w}%`} />
          </SkeletonRow>
        ))}
      </Section>
    );
  }

  if (!recentItems.length) {
    return <EmptyHint>Empezá a escribir para buscar...</EmptyHint>;
  }

  return (
    <Section>
      <SectionHeader>
        {clockIcon}
        <SectionLabel>Recientes</SectionLabel>
      </SectionHeader>

      {recentItems.map(item => (
        <ResultRow key={`recent-${item.type}-${item.id}`} href={item.href}>
          <Icon
            icon={item.type === 'post' ? bookSvg : item.isPast ? videoSvg : calendarSvg}
            size="18px"
            color="#636E72"
          />
          <ResultText>
            <ResultTitle>{item.title}</ResultTitle>
            {item.subtitle && <ResultSubtitle>{item.subtitle}</ResultSubtitle>}
          </ResultText>
        </ResultRow>
      ))}
    </Section>
  );
};

const state = ({ postStore, sessionStore, groupStore }) => ({
  posts: postStore.lastPosts.data?.data ?? null,
  postsLoading: postStore.lastPosts.states.loading,
  sessions: sessionStore.all.data,
  group: groupStore.group.data,
});

export default connect(state)(Component);
